import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { ProductLineLoadingPic } from '@/app/components/icons/ProductLineLoadingPic';
import { ProductLineView } from '@/app/components/icons/ProductLineView';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { DisplaySelectedAudioItem } from '@/app/components/shared/DisplaySelectedAudioItem';
import { InputField } from '@/app/components/shared/ElementDialog/components/InputField';
import { TextAreaField } from '@/app/components/shared/ElementDialog/components/TextAreaField';
import { MAX_INPUT_LENGTH } from '@/app/components/shared/ElementDialog/constants';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import { MultiSelect } from '@/app/components/ui/multi-select';
import { SITE, TTS_VOICE_LIST } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { Option, UpdateInteractionStatusFn } from '@/app/types';
import { cn } from '@/app/utils';
import type { Element, ElementDetail, ElementDetailAction } from '../types';
import { toTitleSectionMode } from '../utils';
import { DeleteBtn } from './DeleteButton';
import { FooterBtnGroup } from './FooterBtnGroup';
import { FormItem } from './FormItem';

interface RightPanelInterface {
  isAuthor: boolean;
  isSubmitSuccess: boolean;
  state: ElementDetail | undefined;
  changeItem: (params: ElementDetailAction) => void;
  categoryOptions: Option<string>[];
  handleCancel: () => void;
  openCreation: (index: number) => void;
  openCreationPost: (open: boolean, index?: number) => void;
  handleSubmit: () => void;
  handleCopy: () => void;
  disableSubmit?: boolean;
  handleUse: () => void;
  onReportEvent: (name: string) => void;
  onCancelSelect: () => void;
  updateVote: UpdateInteractionStatusFn;
  updateUnVote: UpdateInteractionStatusFn;
  handleCollect: UpdateInteractionStatusFn;
  handleUnCollect: UpdateInteractionStatusFn;
  handleClose: () => void;
  handleEdit?: () => void;
  handleSubmitToCommunity?: () => void;
  elementType?: 'unspecified' | 'preset' | 'user';
  // 是否是商用授权
  isCommercial: boolean;
}

export const RightPanel = ({
  isAuthor,
  isSubmitSuccess,
  state,
  changeItem,
  categoryOptions,
  handleCancel,
  openCreation,
  openCreationPost,
  handleSubmit,
  handleCopy,
  disableSubmit,
  handleUse,
  onReportEvent,
  onCancelSelect,
  updateVote,
  updateUnVote,
  handleCollect,
  handleUnCollect,
  handleClose,
  handleEdit,
  handleSubmitToCommunity,
  elementType,
  isCommercial,
}: RightPanelInterface) => {
  const t = useTranslations('elements');

  const { referenceModalMode, referenceModalFrom, referenceModalType, isTeamSpace } = useStore((state) => ({
    referenceModalFrom: state.referenceModalFrom,
    referenceModalMode: state.referenceModalMode,
    referenceModalType: state.referenceModalType,
    isTeamSpace: state.isTeamSpace,
  }));

  const isPostMode = referenceModalMode === 'post';
  const isViewMode = referenceModalMode === 'view';
  const isShareType = referenceModalType === 'share';
  const isSubmitForbidden = SITE === 'china' || state?.voice?.is_my === true;
  const showTip = isPostMode && isAuthor;

  const selectedLabels = useMemo(() => {
    if (!isViewMode) return '';
    return (
      state?.category
        ?.map((v) => {
          const option = categoryOptions.find((opt) => opt.value === v);
          return option ? option.label : v;
        })
        .join(',') ?? '-'
    );
  }, [state?.category, categoryOptions, isViewMode]);

  const renderTypeText = (modality: Element['modality']) => {
    if (!modality || modality === 'unspecified') return null;
    const typeMap = {
      image: t('elementDialog.modality.image'),
      video: t('elementDialog.modality.video'),
      text: t('elementDialog.modality.text'),
    };
    return typeMap[modality as keyof typeof typeMap] || '';
  };

  const renderReadOnlyField = (label: string, value: string | null | undefined, className?: string) => {
    return (
      <div className={className}>
        <div className="mb-1 text-system-white/40 text-xs">{label}</div>
        <div className="rounded-8 bg-transparent">
          <div className="font-semibold text-base text-system-white leading-[1.4]">{value || '-'}</div>
        </div>
      </div>
    );
  };

  const renderTimbreField = () => (
    <div>
      <div className="mb-1 text-system-white/40 text-xs">{t('submit.dialog.form.sound')}</div>
      <div className="rounded-8 bg-transparent">
        {state?.voice ? (
          <DisplaySelectedAudioItem audio={state.voice} className="w-max rounded-full" audioList={TTS_VOICE_LIST} />
        ) : (
          <div className="h-6 font-semibold text-base text-system-white leading-[1.4]">
            {t('submit.dialog.form.blank')}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        'flex w-[500px] flex-col gap-6 bg-[#17191B] pt-12 pr-6 pb-6 pl-8 sm:w-full sm:p-0 sm:px-4 sm:pb-[72px]',
        isViewMode ? 'sm:mt-6' : 'sm:mt-3',
      )}
    >
      <div className="flex-1 space-y-3 overflow-y-auto">
        <div className="space-y-3">
          {/* 投稿模式 */}
          {isPostMode && (
            <>
              {/* Reference Type */}
              <FormItem
                title={t('submit.dialog.form.referenceType')}
                required={true}
                content={
                  <MultiSelect
                    options={categoryOptions}
                    value={state?.category || []}
                    onChange={(val) => {
                      changeItem({ type: 'updateCategories', value: val });
                    }}
                    placeholder={t('submit.dialog.form.typePlaceholder')}
                    maxCountMessage={t('submit.dialog.form.typePickLimitText', {
                      n: 3,
                    })}
                    maxCount={3}
                  />
                }
              />

              {/* Reference Series 输入 */}
              <FormItem
                title={t('submit.dialog.form.series')}
                content={
                  <InputField
                    initialValue={state?.series}
                    value={state?.series}
                    onChange={(e) => changeItem({ type: 'updateSeries', value: e.target.value })}
                    readOnly={false}
                    placeholder={t('submit.dialog.form.seriesPlaceholder')}
                    maxLength={MAX_INPUT_LENGTH}
                  />
                }
              />

              {/* Timbre 展示 - 仅图片主体显示 */}
              {state?.modality === 'image' && (
                <FormItem
                  title={t('submit.dialog.form.sound')}
                  content={
                    state?.voice ? (
                      <DisplaySelectedAudioItem
                        audio={state?.voice}
                        className="w-max rounded-full"
                        audioList={TTS_VOICE_LIST}
                      />
                    ) : (
                      <div className="h-6 font-semibold text-base text-system-white leading-[1.4]">
                        {t('submit.dialog.form.blank')}
                      </div>
                    )
                  }
                />
              )}

              {/* Reference Introduce 输入 */}
              <FormItem
                title={t('submit.dialog.form.introduce')}
                content={
                  <TextAreaField
                    initialValue={state?.introduce}
                    value={state?.introduce}
                    readOnly={false}
                    fieldClassName="bg-transparent"
                    placeholder={t('submit.dialog.form.introducePlaceholder')}
                    maxLength={120}
                    rows={4}
                    onChange={(e) => {
                      changeItem({ type: 'updateIntroduce', value: e.target.value });
                    }}
                  />
                }
              />

              {/* 示例视频上传 */}
              <FormItem
                required={true}
                title={
                  <div className="flex items-center">
                    <div>{t('submit.dialog.form.video')}</div>
                    <CustomTooltip content={t('submit.dialog.form.videoTip')} side="bottom" trigger="auto">
                      <div>
                        <BaseLineInfo className="ml-1 block size-3.5 text-system-white" />
                      </div>
                    </CustomTooltip>
                  </div>
                }
                contentClassName="bg-transparent"
                content={
                  <div className="flex gap-2">
                    {state?.creations?.map((creation, index) => (
                      <div key={creation.id} className="group relative">
                        <MediaWithMaskBg
                          alt="main"
                          src={creation.cover_uri}
                          maskSrc={creation.cover_uri}
                          className="pointer-events-none aspect-square w-[100px] rounded-8 bg-system-black36 sm:pointer-events-auto"
                          disableDownload
                          useCover={true}
                          onClick={() => {
                            onReportEvent('video');
                            openCreation(index);
                          }}
                        />
                        <DeleteBtn
                          className="hidden sm:block"
                          onClick={() => {
                            changeItem({
                              type: 'updateCreations',
                              value: state?.creations?.filter((it) => it.id !== creation.id),
                            });
                          }}
                        />
                        <div className="absolute inset-0 hidden items-center justify-evenly rounded-8 bg-system-black48 group-hover:flex sm:hidden sm:group-hover:hidden">
                          <ProductLineView
                            className="size-5"
                            onClick={() => {
                              onReportEvent('video');
                              openCreation(index);
                            }}
                          />
                          <ProductLineLoadingPic
                            className="size-5"
                            onClick={() => {
                              openCreationPost(true, index);
                            }}
                          />
                          <DeleteBtn
                            onClick={() => {
                              changeItem({
                                type: 'updateCreations',
                                value: state?.creations?.filter((it) => it.id !== creation.id),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    {(state?.creations?.length || 0) < 3 && (
                      <div
                        className="flex h-[100px] w-[100px] cursor-pointer flex-col items-center justify-center rounded-8 bg-system-black36 text-sm text-system-white48"
                        onClick={() => openCreationPost(true)}
                      >
                        <BaseLinePlus className="size-6 text-system-white" />
                      </div>
                    )}
                  </div>
                }
              />
            </>
          )}

          {/* View 模式：共享主体 */}
          {isViewMode && isShareType && (
            <>
              <div className="grid grid-cols-2 gap-5">
                {renderReadOnlyField(t('elementDialog.name'), state?.name)}
                {renderReadOnlyField(t('elementDialog.type'), renderTypeText(state?.modality))}
                {renderReadOnlyField(t('submit.dialog.form.referenceType'), selectedLabels)}
                {renderReadOnlyField(t('submit.dialog.form.series'), state?.series)}
                {state?.modality === 'image' && renderTimbreField()}
              </div>
              {state?.introduce && (
                <FormItem
                  title={t('submit.dialog.form.introduce')}
                  titleClassName="text-system-white/40 text-xs"
                  contentClassName="bg-transparent"
                  content={
                    <div className="whitespace-pre-wrap text-base text-system-white leading-[1.4]">
                      {state.introduce}
                    </div>
                  }
                />
              )}
            </>
          )}

          {/* View 模式：个人主体 */}
          {isViewMode && !isShareType && (
            <>
              <div className="grid grid-cols-2 gap-5">
                {renderReadOnlyField(t('elementDialog.type'), renderTypeText(state?.modality))}
                {state?.modality === 'image' && renderTimbreField()}
              </div>
              {state?.style && (
                <FormItem
                  title={t('elementDialog.style')}
                  titleClassName="text-system-white/40 text-xs"
                  contentClassName="bg-transparent"
                  content={<div className="text-base text-system-white leading-[1.4]">{state.style}</div>}
                />
              )}
              {state?.description && (
                <FormItem
                  title={t('elementDialog.description')}
                  titleClassName="text-system-white/40 text-xs"
                  contentClassName="bg-transparent"
                  content={
                    <div className="whitespace-pre-wrap text-base text-system-white leading-[1.4]">
                      {state.description}
                    </div>
                  }
                />
              )}
            </>
          )}
        </div>
        {/* 后续恢复此行代码 */}
        {/* {(isAuthor || referenceModalMode === 'post') && (
          <div className="flex items-start gap-2 pb-3 sm:pt-2">
            <CheckboxFill
              id="allowCopy"
              checked={state?.allowCopy}
              disabled={referenceModalMode === 'view'}
              className="mt-0.5 disabled:opacity-65 sm:mt-0"
              onCheckedChange={(val) => {
                changeItem({
                  type: 'updateAllowCopy',
                  value: val === 'indeterminate' ? false : val,
                });
              }}
            />
            <Label
              className={cn(
                'flex-1 font-normal text-sm text-system-white sm:text-xs',
                referenceModalMode === 'view' && 'opacity-50',
              )}
              htmlFor="allowCopy"
            >
              {t('submit.dialog.form.allow')}
            </Label>
          </div>
        )} */}
        {SITE === 'china' && <div className="text-system-white/40 text-xs">内容由 AI 生成</div>}
      </div>

      {showTip && (
        <div className="mt-2 hidden items-center px-4 pb-5 sm:flex">
          <div className="line-clamp-1 flex-1 text-system-white36 text-xs">{t('submit.dialog.form.tip')}</div>
          <CustomTooltip content={t('submit.dialog.form.tip')} side="bottom" trigger="auto">
            <div>
              <BaseLineInfo className="block size-3.5 text-system-white48" />
            </div>
          </CustomTooltip>
        </div>
      )}

      <FooterBtnGroup
        className="sm:hidden"
        isTeamSpace={isTeamSpace}
        mode={toTitleSectionMode(referenceModalMode)}
        isAuthor={isAuthor}
        isSubmitSuccess={isSubmitSuccess}
        disableSubmit={disableSubmit}
        isSubmitForbidden={isSubmitForbidden}
        shareElementId={state?.shareElementId}
        reactInfo={state?.reactInfo}
        from={referenceModalFrom}
        allowCopy={state?.allowCopy}
        referenceModalType={referenceModalType}
        elementType={elementType}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        onCopy={handleCopy}
        onUse={handleUse}
        onEdit={handleEdit}
        onSubmitToCommunity={handleSubmitToCommunity}
        updateVote={updateVote}
        updateUnVote={updateUnVote}
        onCancelSelect={onCancelSelect}
        handleCollect={handleCollect}
        handleUnCollect={handleUnCollect}
        canCollect={state?.shareStatus === 'approved'}
        handleClose={handleClose}
        isCommercial={isCommercial}
      />

      {showTip && (
        <div className="mt-3 flex items-center px-4 sm:hidden">
          <div className="line-clamp-1 flex-1 text-system-white36 text-xs">{t('submit.dialog.form.tip')}</div>
          <CustomTooltip content={t('submit.dialog.form.tip')} side="bottom" trigger="auto">
            <div>
              <BaseLineInfo className="block size-3.5 text-system-white48" />
            </div>
          </CustomTooltip>
        </div>
      )}
    </div>
  );
};
